import 'react-dates/initialize';
import pick from 'lodash/pick';

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { datePropType, getAdditionalProps } from '@eventbrite/eds-utils';
import { DEFAULT_INPUT_DISPLAY_FORMAT } from '@eventbrite/eds-utils';

import SingleDatePickerController from './_internal/SingleDatePickerController';

import {
    COLOR_PROP,
    BLUE_COLOR,
    DAY_PICKER_RANGE_CONTROLLER_PROP_TYPES,
} from './constants';

import './datePicker.scss';

export default class SingleDatePicker extends PureComponent {
    static propTypes = {
        /**
         * For a reference to all available properties we forward to
         * `react-dates` check https://github.com/airbnb/react-dates#daterangepicker
         */
        ...DAY_PICKER_RANGE_CONTROLLER_PROP_TYPES,
        /**
         * Initial date
         */
        defaultValue: datePropType,
        /**
         * Current selected date
         */
        value: datePropType,
        /**
         * ID of element
         */
        id: PropTypes.string,
        /**
         * Name of element
         */
        name: PropTypes.string,
        /**
         * Minimum date that can be chosen
         */
        minDate: datePropType,
        /**
         * Maximum date that can be chosen
         */
        maxDate: datePropType,
        /**
         * Set error state
         */
        hasError: PropTypes.bool,
        /**
         * {function} for handling date selection
         */
        onChange: PropTypes.func,
        /**
         * {function} for handling blur event
         */
        onBlur: PropTypes.func,
        /**
         * {function} onFocus
         */
        onFocus: PropTypes.func,
        /**
         * Moment formatting date string to dictate display format of selected
         * dates
         */
        inputDisplayFormat: PropTypes.string,
        /**
         * Color scheme for the calendar component
         */
        color: PropTypes.oneOf(COLOR_PROP),
        /**
         * {function} Determines if a given day should be highlighted on the calendar.
         * The function receives a `moment()` wrapped date as paremeter and should return
         * a Boolean value indicating wheter to highlight the day or not.
         * Example:
         * Highlight Fridays:
         * ```js
         * <DatePicker isDayHighlighted={(day) => day.format('ddd') == 'Fri'}/>
         * ```
         */
        isDayHighlighted: PropTypes.func,
        /**
         * {function} Determines if a given day should be blocked on the calendar.
         * The function receives a `moment()` wrapped date as paremeter and should return
         * a Boolean value indicating wheter to block the day or not.
         * Blocked days are disabled and cannot be selected.
         * Example:
         * Block Weekends:
         * ```js
         * <DatePicker isDayBlocked={(day) => Boolean(day.format('ddd').match(/Sat|Sun/))}/>
         * ```
         */
        isDayBlocked: PropTypes.func,
    };

    static defaultProps = {
        color: BLUE_COLOR,
        inputDisplayFormat: DEFAULT_INPUT_DISPLAY_FORMAT,
    };

    constructor(props) {
        super(props);

        const date = props.value || props.defaultValue;

        this.state = { date };
    }

    UNSAFE_componentWillReceiveProps({ value }) {
        if (value !== undefined && value !== this.state.date) {
            this.setState({ date: value });
        }
    }

    _handleOnChange = (date) => {
        const { onChange } = this.props;

        this.setState({ date }, () => {
            if (onChange) {
                onChange(date);
            }
        });
    };

    render() {
        const {
            id,
            name,
            minDate,
            maxDate,
            hasError,
            onBlur,
            onFocus,
            color,
            inputDisplayFormat,
            value,
            defaultValue,
            isDayHighlighted,
            isDayBlocked,
        } = this.props;
        const { date } = this.state;

        const classes = classNames(
            'eds-date-picker',
            'eds-date-picker--single-date',
            `eds-date-picker--${color}`,
        );

        const extraAttrs = getAdditionalProps(this);
        const dateRangePickerExtraAttrs = pick(
            this.props,
            Object.keys(DAY_PICKER_RANGE_CONTROLLER_PROP_TYPES),
        );

        return (
            <SingleDatePickerController
                {...extraAttrs}
                {...dateRangePickerExtraAttrs}
                inputDisplayFormat={inputDisplayFormat}
                defaultValue={defaultValue}
                value={value}
                containerClasses={classes}
                id={id}
                name={name}
                minDate={minDate}
                maxDate={maxDate}
                hasError={hasError}
                onChange={this._handleOnChange}
                onBlur={onBlur}
                onFocus={onFocus}
                startDate={date}
                endDate={date}
                isDayHighlighted={isDayHighlighted}
                isDayBlocked={isDayBlocked}
            />
        );
    }
}
